import { defineStore } from "pinia";
import { HabilitationsApi } from "@simbelapp/order-sdk";
import type {
  IUpdateUserHabilitation,
  IUpdateUserHabilitationPayload,
  IUserHabilitationDetails,
} from "~/utils/interfaces/habilitations-interfaces";
import { useUsersHabilitationsStore } from "~/store/users-habilitations/users-habilitations.store";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { usePagination } from "~/composables/paginations/usePagination";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";

export const useUpdateUserHabilitationsStore = defineStore("useUpdateUserHabilitationsStore", {
  state: (): IUpdateUserHabilitation => {
    return {
      showToRenewModal: false,
      showToRemoveModal: false,
      userUpdate: {
        userId: null,
        habilitationName: null,
        durationYears: null,
      },
    };
  },
  getters: {},
  actions: {
    async validateUserUpdate() {},

    openUserDetailsModal() {
      this.$reset();
      const uersHabilitationsStore = useUsersHabilitationsStore();
      uersHabilitationsStore.cleanValidationError();
    },

    async updateUserRenew(userHabilitationId: string, toRenew: boolean) {
      const usersHabilitationsStore = useUsersHabilitationsStore();
      const feedback = useFeedback();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
        const data = await apiInstance.updateUserHabilitation({
          userHabilitationId,
          updateUserHabilitationDto: {
            toRenew,
          },
        });
        if (data) {
          feedback.success("Vos modifications ont bien été sauvegardées", "small");
          return data;
        }
      } catch {
        usersHabilitationsStore.updateRow(userHabilitationId, "toRenew", !toRenew);
        feedback.error(`Une erreur est survenue`, "small");
        return null;
      }
    },

    async updateUserHabilitation(userHabilitationDetails: IUserHabilitationDetails) {
      const feedback = useFeedback();
      const usersHabilitationsStore = useUsersHabilitationsStore();
      // Aliased: the parameter carries the EDITED habilitation, the store the PERSISTED one, and
      // `removeDocument` below is precisely the comparison between the two.
      const { userHabilitationDetails: persistedDetails } = usersHabilitationsStore;

      const {
        habilitationId,
        habilitationStatus,
        userDisplayValue,
        userHabilitationId,
        documentLink,
        certificate,
        mainEntityId,
        usersFilter,
        comment,
        ...body
      } = userHabilitationDetails;

      const updateUserHabilitationPayload: IUpdateUserHabilitationPayload = body;
      const { habilitationName, durationYears, expirationDate, renewalDate, toRenew } = updateUserHabilitationPayload;
      if (userHabilitationId) {
        try {
          const removeDocument: boolean = (persistedDetails?.documentLink && !documentLink) || false;
          await this.updateDocument(userHabilitationId, removeDocument, certificate);

          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
          const data = await apiInstance.updateUserHabilitation({
            userHabilitationId,
            updateUserHabilitationDto: {
              durationYears: durationYears ?? undefined,
              expirationDate: expirationDate ? new Date(expirationDate) : null,
              habilitationId,
              habilitationName,
              habilitationStatus: habilitationStatus ?? undefined,
              renewalDate: renewalDate ? new Date(renewalDate) : null,
              toRenew,
              userId: userHabilitationDetails.userId!,
              comment,
            },
          });

          feedback.success("Vos modifications ont bien été sauvegardées", "small");
          return data;
        } catch {
          feedback.error(`Une erreur est survenue lors de l'ajout`, "small");
          return null;
        }
      }
    },

    async updateDocument(userHabilitationId: string, removeDocument: boolean, certificate: File | null) {
      const feedback = useFeedback();
      let data: any = {};
      const usersHabilitationsStore = useUsersHabilitationsStore();
      try {
        if (certificate) {
          data = await usersHabilitationsStore.uploadCertificate(userHabilitationId, certificate);
        } else if (removeDocument) {
          data = await usersHabilitationsStore.removeCertificate(userHabilitationId);
        }
        if (!data) {
          feedback.error(`Une erreur est survenue lors de l'ajout`, "small");
        }
      } catch {
        feedback.error(`Une erreur est survenue lors de l'ajout`, "small");
      }
    },

    async removeUserHabilitation(userHabilitationId: string) {
      const feedback = useFeedback();
      const pagination = usePagination();
      const usersHabilitationsStore = useUsersHabilitationsStore();

      try {
        if (userHabilitationId) {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
          await apiInstance.removeUserHabilitation({
            userHabilitationId,
          });
          await pagination.fetchData();
          this.showToRemoveModal = false;
          usersHabilitationsStore.showUserHabilitationDetailModal = false;
          feedback.success("L'habilitation a bien été supprimée", "small");
        }
      } catch {
        feedback.error(`Une erreur est survenue lors de la suppression`, "small");
      }
    },
  },
});
